import './index.css';

/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/media-has-caption */
import React, { useState, useEffect, useRef } from 'react';
import Hls from 'hls.js';
import { Avatar, Portal } from '@material-ui/core';
import CustomControls from './CustomControls';
import { Close } from '@material-ui/icons';
import config from './hlsconfig';
import { getRooms, getSingleRoomStream } from '@/app/services/liveStreaming/liveStreaming';

/** ===== Tuning ===== */
const STALL_THRESHOLD_SEC = 5;
const REFRESH_MIN_INTERVAL_MS = 45_000;
const BACKOFF_BASE_MS = 5_000;
const BACKOFF_MAX_MS = 60_000;
const ATTACH_GRACE_MS = 3_000; // don't cancel/stop inside first 3s after (re)attach
const WATCHDOG_TICK_MS = 5_000;

const SingleRoomStreaming = ({ streamData, isMultiview = false }) => {
	const [videoBannerText, setVideoBannerText] = useState('Loading');
	const [hlsNotSupported, setHlsNotSupported] = useState(false);
	const [isFullScreenIphone, setIsFullScreenIphone] = useState(false);
	const [showPortraitOverlay, setShowPortraitOverlay] = useState(true);
	const [showControls, setShowControls] = useState(false);

	// Elements/instances
	const hlsRef = useRef(null);
	const hlsListenersWiredRef = useRef(false);
	const playerRef = useRef(null);
	const wrapperRef = useRef(null);
	const hideControlsTimeoutRef = useRef(null);

	// Resiliency + control state
	const currentUrlRef = useRef(streamData?.session_url || null);
	const refreshingRef = useRef(false);
	const lastRefreshAtRef = useRef(0);
	const lastPlaybackTimeRef = useRef(0);
	const lastProgressAtRef = useRef(0);
	const isLiveRef = useRef(false); // becomes true after first LEVEL_LOADED
	const graceUntilRef = useRef(0); // block stop/detach inside this window

	// Backoff
	const retryTimerRef = useRef(null);
	const backoffMsRef = useRef(BACKOFF_BASE_MS);
	const backoffActiveRef = useRef(false);

	// Atomic refresh token
	const refreshSeqRef = useRef(0);

	const clearHideTimer = () => {
		if (hideControlsTimeoutRef.current) {
			clearTimeout(hideControlsTimeoutRef.current);
			hideControlsTimeoutRef.current = null;
		}
	};

	const extractSessionUrl = (res) =>
		res?.data?.data?.session_url ??
		res?.data?.session_url ??
		res?.data?.data?.[0]?.session_url ??
		res?.session_url ??
		null;

	const fetchFreshUrl = async () => {
		const roomId = streamData?.room?.id ?? streamData?.id;
		try {
			if (roomId != null) {
				const r = await getSingleRoomStream(roomId);
				const u = extractSessionUrl(r);
				if (u) return u;
			}
		} catch (e) {
			console.error('Error fetching fresh url', e);
		}
		try {
			const name = streamData?.name ?? streamData?.room?.name ?? '';
			const r2 = await getRooms(name, '', 1);
			const u2 = extractSessionUrl(r2);
			if (u2) return u2;
		} catch (e) {
			console.error('Error fetching fresh url', e);
		}
		return null;
	};

	/** Avoid cancel thrash: don’t stop/detach inside grace window */
	const haltFetching = () => {
		const v = playerRef.current;
		if (Date.now() < graceUntilRef.current) {
			return;
		}
		if (Hls.isSupported()) {
			try {
				hlsRef.current?.stopLoad?.();
			} catch (e) {
				console.error('Error stopping load', e);
			}
		} else if (v) {
			try {
				v.pause?.();
			} catch (e) {
				console.error('Error pausing video', e);
			}
			try {
				v.src = '';
				v.load?.();
			} catch (e) {
				console.error('Error loading video', e);
			}
		}
	};

	/** Single backoff loop at a time */
	const scheduleRefreshAfterBackoff = () => {
		if (retryTimerRef.current || backoffActiveRef.current) return;
		backoffActiveRef.current = true;
		haltFetching();
		setVideoBannerText('Connecting');

		const delay = backoffMsRef.current;
		retryTimerRef.current = setTimeout(async () => {
			retryTimerRef.current = null;
			const fresh = await apiReCall(true, { recreate: true });
			if (fresh) {
				backoffMsRef.current = BACKOFF_BASE_MS;
				backoffActiveRef.current = false;
			} else {
				backoffMsRef.current = Math.min(backoffMsRef.current * 2, BACKOFF_MAX_MS);
				backoffActiveRef.current = false;
				scheduleRefreshAfterBackoff();
			}
		}, delay);
	};

	/** Attach/swap with cache-busting, no-store and listeners-once */
	const swapToUrl = (nextUrl, { recreate = false } = {}) => {
		const video = playerRef.current;
		if (!video || !nextUrl) return;

		const urlWithBust = `${nextUrl}${nextUrl.includes('?') ? '&' : '?'}cb=${Date.now()}`;
		currentUrlRef.current = nextUrl;
		setVideoBannerText('Connecting');
		graceUntilRef.current = Date.now() + ATTACH_GRACE_MS;

		try {
			video.muted = true;
			video.playsInline = true;
		} catch (e) {
			console.error('Error muting video', e);
		}

		if (Hls.isSupported()) {
			setHlsNotSupported(false);

			if (!hlsRef.current || recreate) {
				try {
					hlsRef.current?.destroy?.();
				} catch (e) {
					console.error('Error destroying hls instance', e);
				}
				// Force no-cache on every HLS request
				const fetchSetup = (ctx, init) => {
					try {
						const u = new URL(ctx.url, window.location.href);
						u.searchParams.set('cb', Date.now().toString());
						return new Request(u.toString(), { ...init, cache: 'no-store' });
					} catch (e) {
						console.error('Error creating request', e);
						return new Request(ctx.url, { ...init, cache: 'no-store' });
					}
				};
				hlsRef.current = new Hls({ ...config, debug: false, fetchSetup });
				hlsListenersWiredRef.current = false;
			} else {
				try {
					hlsRef.current.detachMedia();
				} catch (e) {
					console.error('Error detaching media', e);
				}
			}

			if (!hlsListenersWiredRef.current) {
				hlsListenersWiredRef.current = true;

				hlsRef.current.on(Hls.Events.LEVEL_LOADED, (_, data) => {
					isLiveRef.current = !!data?.details?.live || !Number.isFinite(playerRef.current?.duration);
				});

				hlsRef.current.on(Hls.Events.ERROR, async (_, data) => {
					if (!data?.fatal) return;

					// Don’t thrash during grace
					if (Date.now() < graceUntilRef.current) {
						return;
					}

					const code = data?.response?.code;
					const is4xx = code >= 400 && code < 500;

					if (is4xx) {
						scheduleRefreshAfterBackoff();
						return;
					}

					if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
						try {
							hlsRef.current?.recoverMediaError?.();
							return;
						} catch (e) {
							console.error('Error recovering media error', e);
						}
					}

					await apiReCall(true, { recreate: true });
				});
			}

			hlsRef.current.attachMedia(video);
			const onAttached = () => {
				try {
					hlsRef.current.loadSource(urlWithBust);
				} catch (e) {
					console.error('Error loading source', e);
				}
				try {
					hlsRef.current.startLoad();
				} catch (e) {
					console.error('Error starting load', e);
				}
				video.play?.().catch(() => {});
				hlsRef.current.off(Hls.Events.MEDIA_ATTACHED, onAttached);
			};
			hlsRef.current.on(Hls.Events.MEDIA_ATTACHED, onAttached);
		} else if (video.canPlayType('application/vnd.apple.mpegurl')) {
			setHlsNotSupported(true);

			const onVideoError = () => {
				scheduleRefreshAfterBackoff();
			};
			const onStalled = () => {
				setTimeout(() => {
					if (Date.now() - lastProgressAtRef.current > STALL_THRESHOLD_SEC * 1000) {
						scheduleRefreshAfterBackoff();
					}
				}, 2000);
			};
			video.addEventListener('error', onVideoError, { once: true });
			video.addEventListener('stalled', onStalled, { once: true });

			try {
				video.pause?.();
			} catch (e) {
				console.error('Error pausing video', e);
			}
			try {
				video.src = '';
				video.load?.();
			} catch (e) {
				console.error('Error loading video', e);
			}
			video.src = urlWithBust;
			video.load?.();
			video.play?.().catch(() => {});
		}

		// reset stall tracking
		lastPlaybackTimeRef.current = video.currentTime || 0;
		lastProgressAtRef.current = Date.now();
	};

	/** Atomic, debounced refresh; applies only if still newest */
	const apiReCall = async (bypassDebounce = false, { recreate = false } = {}) => {
		const now = Date.now();
		if (!bypassDebounce && (refreshingRef.current || now - lastRefreshAtRef.current < REFRESH_MIN_INTERVAL_MS)) {
			return null;
		}
		if (refreshingRef.current) {
			return null;
		}
		refreshingRef.current = true;
		const mySeq = ++refreshSeqRef.current;

		try {
			console.log('fetching fresh url');
			const fresh = await fetchFreshUrl();
			lastRefreshAtRef.current = Date.now();

			if (!fresh) return null;
			if (refreshSeqRef.current !== mySeq) return null;

			swapToUrl(fresh, { recreate });
			return fresh;
		} catch (e) {
			console.error('Error fetching fresh url', e);
			return null;
		} finally {
			refreshingRef.current = false;
		}
	};

	/** Entry point */
	const ensurePlayback = async () => {
		let src = streamData?.session_url || currentUrlRef.current;

		if (!src) {
			src = await fetchFreshUrl();
			console.log('fetched fresh url', src);
			currentUrlRef.current = src;
		}
		if (src) {
			swapToUrl(src, { recreate: !hlsRef.current && Hls.isSupported() });
		} else {
			scheduleRefreshAfterBackoff();
		}
	};

	/** Cleanup */
	const cleanupHls = () => {
		if (retryTimerRef.current) {
			clearTimeout(retryTimerRef.current);
			retryTimerRef.current = null;
		}
		if (hlsRef.current) {
			try {
				hlsRef.current.destroy();
			} catch (e) {
				console.error('Error destroying hls instance', e);
			}
			hlsRef.current = null;
			hlsListenersWiredRef.current = false;
		}
	};

	/** Re-init when upstream URL changes */
	useEffect(() => {
		currentUrlRef.current = streamData?.session_url || null;
		ensurePlayback();
		return () => cleanupHls();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [streamData?.session_url]);

	/** Rebind on fullscreen Portal swap (new <video>) */
	useEffect(() => {
		const raf = requestAnimationFrame(ensurePlayback);
		return () => cancelAnimationFrame(raf);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isFullScreenIphone]);

	/** Media signals */
	const onWaiting = () => {
		setVideoBannerText('Connecting');
	};
	const onPlaying = () => {
		setVideoBannerText('Live');
		if (retryTimerRef.current) {
			clearTimeout(retryTimerRef.current);
			retryTimerRef.current = null;
		}
		backoffMsRef.current = BACKOFF_BASE_MS;
		backoffActiveRef.current = false;
		// small grace right after we start playing to avoid bitrate-switch thrash
		graceUntilRef.current = Date.now() + 1_000;
	};
	const onTimeUpdate = () => {
		const v = playerRef.current;
		const t = v?.currentTime ?? 0;
		if (t > lastPlaybackTimeRef.current + 0.2) {
			lastPlaybackTimeRef.current = t;
			lastProgressAtRef.current = Date.now();
		}
	};

	/** Stall watchdog */
	useEffect(() => {
		const id = setInterval(() => {
			if (!isLiveRef.current) return;
			if (document.hidden) return; // don't fight background throttling
			const v = playerRef.current;
			if (!v || v.paused || v.seeking) return;

			const since = Date.now() - lastProgressAtRef.current;
			const bufEnd = v?.buffered?.length ? v.buffered.end(v.buffered.length - 1) : 0;
			if (bufEnd - (v.currentTime ?? 0) > 1) return; // we do have buffer → not starved
			if (since > STALL_THRESHOLD_SEC * 1000 && Date.now() > graceUntilRef.current) {
				scheduleRefreshAfterBackoff();
			}
		}, WATCHDOG_TICK_MS);
		return () => clearInterval(id);
	}, []);

	/** Controls show/hide */
	const onInteract = () => {
		setShowControls(true);
		clearHideTimer();
		hideControlsTimeoutRef.current = setTimeout(() => setShowControls(false), 2000);
	};

	useEffect(
		() => () => {
			if (hideControlsTimeoutRef.current) clearTimeout(hideControlsTimeoutRef.current);
		},
		[]
	);

	const isLive = () => videoBannerText === 'Live';
	const handleIphoneFullScreenToggle = () => {
		setIsFullScreenIphone((v) => !v);
		setVideoBannerText('Connecting');
	};

	const VideoEl = (
		<video
			ref={playerRef}
			autoPlay
			className={`${isMultiview ? '' : 'rounded-lg'} video-player object-fill w-full h-full`}
			controls={hlsNotSupported}
			controlsList="nodownload noplaybackrate"
			muted
			playsInline
			poster={streamData?.first_image}
			preload="true"
			onWaiting={onWaiting}
			onPlaying={onPlaying}
			onTimeUpdate={onTimeUpdate}
			onClick={onInteract}
			onMouseMove={onInteract}
			onTouchStart={onInteract}
		/>
	);

	return (
		<div className="relative">
			<div
				className={`mx-auto absolute top-0 font-bold flex items-center gap-8 text-white py-8 z-99 ${hlsNotSupported ? '' : 'ml-10'}`}
				style={hlsNotSupported ? { left: '50%', transform: 'translateX(-50%)' } : {}}
			>
				<Avatar src={streamData?.room?.thumb ?? streamData?.room?.thumb_url} />
				<div>{streamData?.room?.name ?? streamData?.name}</div>
			</div>

			<div className="flex justify-center items-center aspect-ratio-16-9 relative">
				{!isFullScreenIphone ? (
					<div
						ref={wrapperRef}
						className={`w-full ${isMultiview ? '' : 'rounded-lg video-player-box'} overflow-hidden relative`}
						onMouseMove={onInteract}
						onTouchStart={onInteract}
					>
						{VideoEl}

						{streamData?.overlay_image && (streamData?.session_url || currentUrlRef.current) ? (
							<img
								alt=""
								className="img-overlay-blur absolute inset-0 z-50 w-full h-full object-fill"
								id="overlayImage"
								src={streamData?.overlay_image}
							/>
						) : null}

						{(streamData?.session_url || currentUrlRef.current) && !hlsNotSupported ? (
							<>
								<div className={`btn_live ${isLive() ? '' : 'not_btn_live'} z-50`} style={{ top: 14, right: 10 }}>
									{videoBannerText || 'Live'}
									<span className={`${isLive() ? 'live-icon' : 'not-live-icon'}`} />
								</div>
								{!isMultiview ? (
									<CustomControls
										boxRef={wrapperRef}
										handleIphoneFullScreenToggle={handleIphoneFullScreenToggle}
										hlsNotSupported={hlsNotSupported}
										live={true}
										playerRef={playerRef}
										room={streamData}
										showControls={showControls}
									/>
								) : null}
							</>
						) : null}

						{!streamData?.session_url && !currentUrlRef.current ? (
							<div className="stream-not-running-text">Live Streaming Is Not Available</div>
						) : null}
					</div>
				) : null}
			</div>

			{isFullScreenIphone ? (
				<Portal container={document.body}>
					<div
						ref={wrapperRef}
						className="flex items-center justify-center top-0 left-0 h-full w-full fixed z-999 bg-black"
						onMouseMove={onInteract}
						onTouchStart={onInteract}
					>
						{showPortraitOverlay ? (
							<div
								className="fixed sm:hidden h-screen w-screen z-999 flex items-center justify-center"
								style={{ background: 'rgba(0, 0, 0, .75)' }}
							>
								<button
									className="p-4 absolute top-72 right-16 text-white"
									onClick={() => setShowPortraitOverlay(false)}
									type="button"
								>
									<Close />
								</button>
								<p className="text-white">For the best viewing experience, please rotate your device.</p>
							</div>
						) : null}

						<div className="relative w-full h-full flex items-center justify-center">
							<div className="w-full h-full relative bg-black">
								{VideoEl}

								{streamData?.overlay_image ? (
									<img
										alt=""
										className="absolute top-0 left-0 z-99 w-full h-full object-fill"
										src={streamData?.overlay_image}
									/>
								) : null}

								<div className="flex items-center w-full h-48 absolute top-16 pl-16">
									<div
										className={`absolute top-0 font-bold flex items-center gap-8 text-white z-99 ${hlsNotSupported ? '' : 'ml-10'}`}
										style={hlsNotSupported ? { left: '50%', transform: 'translateX(-50%)' } : {}}
									>
										<Avatar src={streamData?.thumb} />
										<div>{streamData?.room?.name ?? streamData?.name}</div>
									</div>
									<div className={`btn_live ${isLive() ? '' : 'not_btn_live'} z-20`}>
										{videoBannerText || 'Live'}
										<span className={`${isLive() ? 'live-icon' : 'not-live-icon'}`} />
									</div>
								</div>

								<CustomControls
									boxRef={wrapperRef}
									handleIphoneFullScreenToggle={handleIphoneFullScreenToggle}
									hlsNotSupported={hlsNotSupported}
									live={true}
									playerRef={playerRef}
									room={streamData}
									showControls={showControls}
								/>
							</div>
						</div>
					</div>
				</Portal>
			) : null}
		</div>
	);
};

export default SingleRoomStreaming;
